import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type {
  EntryParty,
  Party,
  PartyAlias,
  PartyDetail,
  PartyMention,
  PartyMergeResponse,
  PartyRole,
  PartyType,
} from '../types/api';

export const partiesKeys = {
  all: ['parties'] as const,
  list: (projectId: string | null, filters: ListPartiesFilters) =>
    [...partiesKeys.all, 'list', projectId, filters] as const,
  search: (projectId: string | null, q: string) =>
    [...partiesKeys.all, 'search', projectId, q] as const,
  detail: (projectId: string | null, id: string | null) =>
    [...partiesKeys.all, 'detail', projectId, id] as const,
  mentions: (projectId: string | null, id: string | null) =>
    [...partiesKeys.all, 'mentions', projectId, id] as const,
  entryParties: (projectId: string | null, entryId: string) =>
    ['entry-parties', projectId, entryId] as const,
};

export interface ListPartiesFilters {
  q?: string | undefined;
  party_type?: PartyType | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export interface PartyListRow extends Party {
  alias_count: number;
  mention_count: number;
}

export function usePartiesList(filters: ListPartiesFilters = {}) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: partiesKeys.list(projectId, filters),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ parties: PartyListRow[] }>('/parties', { ...filters });
      return res.parties;
    },
  });
}

export function useParty(id: string | null | undefined) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: partiesKeys.detail(projectId, id ?? null),
    enabled: !!id && !!projectId,
    queryFn: async () => api.get<PartyDetail>(`/parties/${id!}`),
  });
}

export function usePartyMentions(id: string | null | undefined) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: partiesKeys.mentions(projectId, id ?? null),
    enabled: !!id && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ mentions: PartyMention[] }>(`/parties/${id!}/mentions`);
      return res.mentions;
    },
  });
}

export function useUpdateParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<{ canonical_name: string; party_type: PartyType; notes: string | null }>;
    }) => api.patch<{ party: Party }>(`/parties/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useSoftDeleteParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/parties/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useAddAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ party_id, alias }: { party_id: string; alias: string }) =>
      api.post<{ alias: PartyAlias }>(`/parties/${party_id}/aliases`, { alias }),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useRemoveAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ party_id, alias_id }: { party_id: string; alias_id: string }) =>
      api.delete<void>(`/parties/${party_id}/aliases/${alias_id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useMergeParties() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ survivor_id, absorb_id }: { survivor_id: string; absorb_id: string }) =>
      api.post<PartyMergeResponse>(`/parties/${survivor_id}/merge`, { absorb_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useSearchParties(q: string) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: partiesKeys.search(projectId, q),
    enabled: !!projectId,
    queryFn: async () => {
      const res = await api.get<{ parties: Party[] }>('/parties', {
        q: q || undefined,
        limit: 50,
      });
      return res.parties;
    },
  });
}

export function useCreateParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      canonical_name: string;
      party_type?: PartyType;
      notes?: string | null;
    }) => api.post<{ party: Party }>('/parties', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: partiesKeys.all }),
  });
}

export function useEntryParties(entryId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: entryId ? partiesKeys.entryParties(projectId, entryId) : ['entry-parties', 'none'],
    enabled: !!entryId && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ entry_parties: EntryParty[] }>('/runsheet-entry-parties', {
        entry_id: entryId ?? undefined,
      });
      return res.entry_parties;
    },
  });
}

export function useAttachParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      entry_id: string;
      party_id: string;
      role: PartyRole;
      party_order?: number;
    }) => api.post<{ entry_party: EntryParty }>('/runsheet-entry-parties', input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['entry-parties'],
      });
      void vars;
    },
  });
}

export function useDetachParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/runsheet-entry-parties/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['entry-parties'] }),
  });
}
